import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import {
  ColumnOrder,
  asColumnOrder,
  cleanUpColumnOrder,
} from 'module/ui/components/molecules/column-order';
import { ColDef, ColumnState, GridApi } from 'ag-grid-community';
import { CompanyFields } from 'helpers/fields/company-fields/fields';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  fields: FilterableField[];
  gridApi?: GridApi;
  isFieldsLoading: boolean;
  toColumnDefs: (fields: FilterableField[]) => ColDef[];
}
interface Result {
  currentColumnOrder: ColumnOrder[];
  defaultColumnOrder: ColumnOrder[];
  changeCurrentColumnOrder: (next: ColumnOrder[]) => void;
  changeColumnOrderFromState: (next: ColumnState[]) => void;
}

export const DEFAULT_FIELD_LIST = [
  'companyName',
  'accountOwner',
  CompanyFields.recentRiskSignals,
  CompanyFields.recentOpportunitySignals,
  CompanyFields.recentOperationalSignals,
  'description',
  'companyUrl',
  'annualizedRecurringRevenue',
  'employees',
  'companyIndustry',
  'companyStage',
  'lastActivityDate',
  'lastMilestoneCSAT',
  'csatRating',
  'customerMembers',
  'trackedMinutes',
  'billableMinutes',
  'actualRevenue',
  'actualCost',
  'actualProfit',
  'actualMargin',
  'champions',
];

const relativelySortColumns = (source: ColDef[]): ColDef[] => {
  const order = Object.fromEntries(DEFAULT_FIELD_LIST.map((v, i) => [v, i]));

  return Array.from(source).sort(
    (a, b) =>
      (order[a.field!] ?? Number.MAX_VALUE) -
      (order[b.field!] ?? Number.MAX_VALUE),
  );
};

/**
 * @description Hook to fetch, manage and manipluate column order in customer portfolio list
 */
export const useColumnOrder = ({
  fields,
  gridApi,
  toColumnDefs,
}: Props): Result => {
  const defaultColumnOrder: ColumnOrder[] = useMemo(
    () =>
      asColumnOrder(
        relativelySortColumns(toColumnDefs(fields)).map((x) => ({
          ...x,
          colId: x.field!,
        })),
      ),
    [fields, toColumnDefs],
  );
  const [columnOrder, setColumnOrder] =
    useState<ColumnOrder[]>(defaultColumnOrder);

  const changeCurrentColumnOrder = useCallback(
    (changedColumnOrder: ColumnOrder[]) => {
      setColumnOrder(
        cleanUpColumnOrder(defaultColumnOrder)(changedColumnOrder),
      );
    },
    [defaultColumnOrder],
  );
  const changeColumnOrderFromState = useCallback(
    (columnState: ColumnState[]) => {
      setColumnOrder(asColumnOrder(columnState));
    },
    [],
  );

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi?.applyColumnState({ state: columnOrder, applyOrder: true });
  }, [columnOrder, gridApi]);

  return {
    currentColumnOrder: columnOrder,
    defaultColumnOrder,
    changeColumnOrderFromState,
    changeCurrentColumnOrder,
  };
};
